import { ArrowDownTrayIcon, ClockIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { StripeHistoryModal } from 'components/Modals/Payment/StripeHistoryModal'
import { API_STRIPE_INTENT, appApiUrl, itemCountPerPage } from 'config'
import { StripeStatusLabels } from 'pages/Loan/OrderAppraisals'
import { LoanProcessRushPaymentRequestTypeTitles } from 'pages/LoanSubmission/constants'
import { DownloadRangeModal } from 'pages/Tasks/Modals/downloadRange'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  type IStripeIntent,
  getPaymentIntentDashboardUrl,
  getStripeIntents,
  PaymentOrderType,
  PaymentOrderTypeTitles,
  PaymentProvider,
  PaymentProviderTitles,
  PaymentRefIdTitles,
} from 'services'
import { svgLoading } from 'stories/assets'
import { Input2, MultiSelect, Pagination } from 'stories/components'
import { formatTime, getLoanDisplayNo, serialize, thousandSeperator } from 'utils'

import { StripeTemplates } from './StripeTemplates'

/** Every stored StripeIntentStatus, keyed for MultiSelect (value → label). */
export const StripeStatusFilterOptions: Record<string, string> = {
  requires_payment_method: 'Incomplete',
  processing: 'Processing',
  partially_funded: 'Partially Funded',
  requires_action: 'Requires Action',
  canceled: 'Cancelled',
  payment_failed: 'Failed',
  succeeded: 'Succeeded',
  refunded: 'Refunded',
  partially_refunded: 'Partially Refunded',
  created: 'Created',
  requires_capture: 'Requires Capture',
  requires_confirmation: 'Requires Confirmation',
}

const allStatusKeys = Object.keys(StripeStatusFilterOptions)

const getSelectedStatuses = (statusFilter: Record<string, boolean> = {}): string[] =>
  allStatusKeys.filter((key) => statusFilter[key] !== false)

export const StripeManagements = () => {
  const auth = useSelector((state: any) => state.auth)

  const [view, setView] = useState<'list' | 'templates'>('list')
  const [isLoading, setLoading] = useState(true)
  const [filters, setFilters] = useState<Record<string, any>>({
    query: '',
    status: {},
    pageNum: 0,
  })
  const [filterQuery, setFilterQuery] = useState(filters.query)
  const [total, setTotal] = useState(0)
  const [data, setData] = useState<IStripeIntent[]>([])
  const [isDownloadRangeModal, setDownloadRangeModal] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [historyItem, setHistoryItem] = useState<Record<string, any>>({})

  useEffect(() => {
    const timeOutId = setTimeout(() => !isLoading && onChangeFilter('pageNum', 0), 700)
    return () => clearTimeout(timeOutId)
  }, [filterQuery])

  useEffect(() => {
    filterData(filters)
  }, [])

  const filterData = async (filters: Record<string, any>) => {
    setLoading(true)
    const selectedStatuses = getSelectedStatuses(filters.status)

    if (selectedStatuses.length === 0) {
      setData([])
      setTotal(0)
      setLoading(false)
      return
    }

    const params: Record<string, any> = {
      query: filters.query.trim(),
      count: itemCountPerPage,
      skip: filters.pageNum * itemCountPerPage,
      hasData: true,
    }
    if (selectedStatuses.length < allStatusKeys.length) {
      params.status = selectedStatuses
    }

    const { data, total } = await getStripeIntents(params)
    setData(data)
    setTotal(total)
    setLoading(false)
  }

  const onChangeFilter = (key: string, value: any) => {
    if (key === 'status') {
      setFilters((prev) => ({ ...prev, status: value }))
      return
    }
    const newFilters = Object.assign({}, filters)
    newFilters[key] = value
    setFilters(newFilters)
    if (key === 'query') setFilterQuery(value)
    else filterData(newFilters)
  }

  const onStatusBlur = () => {
    setFilters((prev) => {
      const newFilters = { ...prev, pageNum: 0 }
      filterData(newFilters)
      return newFilters
    })
  }

  const onPageNavigate = (num: number) => {
    onChangeFilter('pageNum', num)
  }

  const onDownloadStart = ({ from, to }: { from: string; to: string }) => {
    const selectedStatuses = getSelectedStatuses(filters.status)
    const downloadParams: Record<string, any> = {
      query: filters.query,
      from,
      to,
    }
    if (selectedStatuses.length === 0) {
      // Empty string so serialize emits status= and the backend returns no rows
      downloadParams.status = ''
    } else if (selectedStatuses.length < allStatusKeys.length) {
      downloadParams.status = selectedStatuses
    }
    const link = `${appApiUrl}${API_STRIPE_INTENT}/download?token=${auth.token}&${serialize(downloadParams)}`
    window.open(link, '_blank')
    setDownloadRangeModal(false)
  }

  const renderDetails = (item: IStripeIntent) => {
    let orderType = PaymentOrderTypeTitles[item.orderType]

    if (item.orderType == PaymentOrderType.LoanSubmissionRush && item.data)
      orderType = LoanProcessRushPaymentRequestTypeTitles[item.data.requestType || ''] || item.data.feeTitle

    let content
    const refTitle = PaymentRefIdTitles[item.orderType]

    if (item.orderType == PaymentOrderType.CounterPartyApproval && item.data) {
      content = `${item.loan?.userEmail} - ${item.data.feeTitle}`
    } else content = `${refTitle}: ${item.refId}`

    return (
      <div>
        <p>Order Type: {orderType}</p>
        {!!refTitle && <p>{content}</p>}
      </div>
    )
  }

  if (view === 'templates') {
    return (
      <div className="StripeManage-container relative">
        <div className="flex items-center gap-4 mb-4">
          <button className="btn-link text-sm" onClick={() => setView('list')}>
            &larr; Back to Stripe Management
          </button>
        </div>
        <StripeTemplates />
      </div>
    )
  }

  return (
    <div className="StripeManage-container relative">
      <LayoutLoading show={isLoading} />
      <h2 className="text-2xl font-bold flex items-center mb-3">
        Stripe Management
        {isLoading && (
          <span className="ml-3">
            <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
          </span>
        )}
      </h2>

      <div className="flex flex-wrap justify-between mb-3">
        <div className="flex items-center flex-wrap gap-3">
          <div className="md:w-96 w-72">
            <Input2
              type="search"
              title="Search"
              hasIcon
              icon={<MagnifyingGlassIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />}
              value={filters.query}
              onChange={(value) => onChangeFilter('query', value)}
            />
          </div>
          <div className="md:w-72 w-56">
            <MultiSelect
              id="stripe-status"
              title="Status"
              options={StripeStatusFilterOptions}
              value={filters.status}
              onChange={(value) => onChangeFilter('status', value)}
              onBlur={onStatusBlur}
            />
          </div>
          <p className="text-[15px]">- {total} Stripe Intents</p>
        </div>
        <div className="flex items-center">
          <button className="btn-link text-sm" onClick={() => setView('templates')}>
            Manage Templates
          </button>
        </div>
      </div>

      <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
        <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6">
          <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="px-2 py-3">
                No
              </th>
              <th scope="col" className="px-2 py-3">
                Intent Id
              </th>
              <th scope="col" className="px-2 py-3">
                Provider
              </th>
              <th scope="col" className="px-2 py-3">
                Status
              </th>
              <th scope="col" className="px-2 py-3">
                Created At
              </th>
              <th scope="col" className="px-2 py-3">
                Customer Email
              </th>
              <th scope="col" className="px-2 py-3">
                Amount
              </th>
              <th scope="col" className="px-2 py-3">
                Loan Number
              </th>
              <th scope="col" className="px-2 py-3">
                Detail
              </th>
              <th scope="col" className="px-2 py-3">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="">
            {data.map((item, index: number) => {
              const status = StripeStatusLabels[item.status] || []
              return (
                <tr className={`border-b ${index % 2 ? 'bg-slate-50' : 'bg-white'}`} key={`${index}`}>
                  <td scope="row" className="px-2 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                    {filters.pageNum * itemCountPerPage + index + 1}
                  </td>
                  <td className="px-2">
                    <a
                      className="font-bold text-shade-blue hover:underline cursor-pointer"
                      href={getPaymentIntentDashboardUrl(item)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <span>{item.intentId}</span>
                    </a>
                  </td>
                  <td className="px-2 capitalize">
                    {PaymentProviderTitles[(item.provider as PaymentProvider) || PaymentProvider.stripe] ||
                      item.provider ||
                      'Stripe'}
                  </td>
                  <td className={`px-2 text-${status[0]}-500`}>{status[1] || item.status}</td>
                  <td className="px-2">{formatTime(item.createdAt)}</td>
                  <td className="px-2">{item.email}</td>
                  <td className="px-2">${thousandSeperator(item.amount)}</td>
                  <td className="px-2">{item.loan ? getLoanDisplayNo(item.loan) : ''}</td>
                  <td className="px-2 py-2">{renderDetails(item)}</td>
                  <td className="px-2">
                    <div className="flex items-center justify-center gap-2">
                      <span
                        className="text-gray-500 cursor-pointer hover-shadow1 p-1 rounded"
                        onClick={() => {
                          setHistoryItem(item)
                          setShowHistory(true)
                        }}
                      >
                        <ClockIcon className="w-4 h-4" />
                      </span>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="footer flex justify-end items-center mt-3 mb-3">
        <Pagination
          totalCount={total}
          itemCountPerPage={itemCountPerPage}
          onNavigate={onPageNavigate}
          pageNum={filters.pageNum}
        />
        <span className="my-2 mr-5 px-2 py-1 rounded hover-shadow1" onClick={() => setDownloadRangeModal(true)}>
          <ArrowDownTrayIcon className="h-5 w-5 text-primary" />
        </span>
      </div>
      {isDownloadRangeModal && (
        <DownloadRangeModal
          title="Select range to download stripe"
          onClose={() => setDownloadRangeModal(false)}
          onDownload={onDownloadStart}
        />
      )}
      {showHistory && (
        <StripeHistoryModal
          history={historyItem.history}
          createdAt={historyItem.createdAt}
          email={historyItem.email}
          amount={historyItem.amount}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  )
}
